import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar from "../components/Sidebar";
import {
  Dashboard6Styles, ModalShell, Field, PagePreloader, Avatar, StatusChip, CardHead, PageHeader, KpiStrip,
} from "../components/ModalUI";
import {
  INSTITUTIONS_LIST,
  POPULAR_BANKS_LIST,
  STATES_LIST,
  KATSINA_LGAS,
  ACADEMIC_SESSIONS_LIST,
  LEVELS_LIST,
  SCHOLARSHIP_DURATIONS_LIST,
  DEFAULT_MOCK_SCHOLARSHIPS,
  DEFAULT_MOCK_PAYMENTS,
  normalizeScholarship
} from "../data/mockScholarships";

const STORAGE_KEY = "zangon_scholarships";
const PAYMENTS_KEY = "zangon_scholarship_payments";

const AVATAR_COLORS = [
  "bg-light-primary text-primary",
  "bg-light-success text-success",
  "bg-light-warning text-warning",
  "bg-light-danger text-danger",
  "bg-light-info text-info"
];

function toTitleCase(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getInitials(name = "") {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function fmtCurrency(amount) {
  return `₦${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
}

export default function ScholarshipManagement() {
  const navigate = useNavigate();
  const [scholarships, setScholarships] = useState([]);
  const [pageReady, setPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  // Modal Refs
  const addModalRef = useRef(null);
  const editModalRef = useRef(null);
  const addFileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  // Active items for editing
  const [selectedScholarship, setSelectedScholarship] = useState(null);

  // Form Fields
  const blankForm = {
    serial_number: "",
    academic_session: "2025/2026",
    institution_name: INSTITUTIONS_LIST[0] || "FUT Babura",
    custom_institution_name: "",
    student_name: "",
    registration_number: "",
    state_of_origin: "Katsina",
    lga: "Zango",
    course: "",
    level: "200",
    scholarship_duration: "4 yrs",
    year_of_entry: 2024,
    year_of_graduation: 2028,
    tuition_or_reg_fees: "",
    allowance: "",
    total_amount: "",
    bank_name: POPULAR_BANKS_LIST[0] || "Zenith Bank",
    custom_bank_name: "",
    account_number: "",
    remark: "Active scholar in good academic standing",
    imageUrl: "",
    imagePreview: null,
    phone: "",
    email: ""
  };
  const [form, setForm] = useState(blankForm);

  // Theme & Metronic init
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest("[data-kt-value]");
      if (!el) return;
      const mode = el.getAttribute("data-kt-value");
      if (mode) {
        if (window.KTThemeMode?.setMode) window.KTThemeMode.setMode(mode);
        else {
          document.documentElement.setAttribute("data-bs-theme", mode);
          localStorage.setItem("kt_theme_mode", mode);
        }
      }
    };
    document.addEventListener("click", onClick);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };

    init();
    const t = setTimeout(init, 0);
    return () => {
      document.removeEventListener("click", onClick);
      clearTimeout(t);
    };
  }, []);

  // Load from local storage or seed default mock data with schema normalization
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    let loadedSch = [];
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          loadedSch = parsed;
        }
      } catch {
        loadedSch = [];
      }
    }

    const existingIds = new Set(loadedSch.map(s => s.id));
    const toAdd = DEFAULT_MOCK_SCHOLARSHIPS.filter(s => !existingIds.has(s.id));
    const mergedSch = (loadedSch.length === 0 ? DEFAULT_MOCK_SCHOLARSHIPS : [...loadedSch, ...toAdd]).map(
      (s, idx) => normalizeScholarship(s, idx)
    );

    localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedSch));
    setScholarships(mergedSch);

    const savedPayments = localStorage.getItem(PAYMENTS_KEY);
    let loadedPay = [];
    if (savedPayments) {
      try {
        const parsedPay = JSON.parse(savedPayments);
        if (Array.isArray(parsedPay) && parsedPay.length > 0) {
          loadedPay = parsedPay;
        }
      } catch {
        loadedPay = [];
      }
    }
    if (loadedPay.length === 0) {
      localStorage.setItem(PAYMENTS_KEY, JSON.stringify(DEFAULT_MOCK_PAYMENTS));
    }

    setTimeout(() => {
      setIsLoading(false);
      setPageReady(true);
    }, 300);
  }, []);

  const saveToStorage = (updated) => {
    setScholarships(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  // Image Selection Handler
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size must be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((prev) => ({
        ...prev,
        imageUrl: ev.target.result,
        imagePreview: ev.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // Stats Calculations
  const stats = useMemo(() => {
    const active = scholarships.filter((s) => s.status === "Active");
    const totalDisbursed = scholarships.reduce((sum, s) => sum + (Number(s.totalDisbursed) || 0), 0);
    const totalBudget = active.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
    return {
      total: scholarships.length,
      active: active.length,
      disbursed: totalDisbursed,
      totalBudget
    };
  }, [scholarships]);

  // Filtered List
  const filtered = useMemo(() => {
    return scholarships.filter((s) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (s.student_name || "").toLowerCase().includes(q) ||
        (s.registration_number || "").toLowerCase().includes(q) ||
        (s.institution_name || "").toLowerCase().includes(q) ||
        (s.course || "").toLowerCase().includes(q) ||
        (s.lga || "").toLowerCase().includes(q) ||
        (s.state_of_origin || "").toLowerCase().includes(q) ||
        (s.serial_number || "").toLowerCase().includes(q) ||
        (s.bank_name || "").toLowerCase().includes(q) ||
        (s.account_number || "").toLowerCase().includes(q);

      const matchSession = sessionFilter === "all" || s.academic_session === sessionFilter;
      const matchStatus = statusFilter === "all" || s.status === statusFilter;
      const matchState = stateFilter === "all" || s.state_of_origin === stateFilter;
      return matchSearch && matchSession && matchStatus && matchState;
    });
  }, [scholarships, search, sessionFilter, statusFilter, stateFilter]);

  // Open Add Modal
  const handleOpenAddModal = () => {
    const nextIndex = scholarships.length + 1;
    const autoSerial = `SCH-2025-${String(nextIndex).padStart(3, "0")}`;
    setForm({
      ...blankForm,
      serial_number: autoSerial,
      tuition_or_reg_fees: 120000,
      allowance: 60000,
      total_amount: 180000,
      year_of_entry: 2024,
      year_of_graduation: 2028
    });
    if (window.bootstrap?.Modal && addModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(addModalRef.current).show();
    }
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();

    const finalInstitution = form.institution_name === "Other / External Institution"
      ? (form.custom_institution_name || "").trim()
      : form.institution_name;

    const finalBank = form.bank_name === "Other"
      ? (form.custom_bank_name || "").trim()
      : form.bank_name;

    const tuition = Number(form.tuition_or_reg_fees) || 0;
    const allowance = Number(form.allowance) || 0;
    const computedTotal = tuition + allowance;

    if (!form.student_name.trim()) {
      toast.error("Please enter the student's full name.");
      return;
    }
    if (!finalInstitution) {
      toast.error("Please select or enter an institution / school name.");
      return;
    }
    if (!form.registration_number.trim()) {
      toast.error("Please enter a registration / matric number.");
      return;
    }
    if (computedTotal <= 0) {
      toast.error("Please enter tuition or allowance fee amounts.");
      return;
    }

    const nextIndex = scholarships.length + 1;
    const autoSerial = form.serial_number.trim() || `SCH-2025-${String(nextIndex).padStart(3, "0")}`;
    const initials = getInitials(form.student_name);
    const avatarClass = AVATAR_COLORS[scholarships.length % AVATAR_COLORS.length];

    const newRecord = normalizeScholarship({
      id: `sch_${Date.now()}`,
      serial_number: autoSerial,
      academic_session: form.academic_session || "2025/2026",
      institution_name: finalInstitution,
      student_name: form.student_name.trim(),
      registration_number: form.registration_number.trim().toUpperCase(),
      state_of_origin: form.state_of_origin || "Katsina",
      lga: form.lga || "Zango",
      course: form.course.trim() || "General Studies",
      level: form.level || "100",
      scholarship_duration: form.scholarship_duration || "4 yrs",
      year_of_entry: Number(form.year_of_entry) || 2024,
      year_of_graduation: Number(form.year_of_graduation) || 2028,
      tuition_or_reg_fees: tuition,
      allowance: allowance,
      total_amount: computedTotal,
      bank_name: finalBank || "Zenith Bank",
      account_number: form.account_number.trim() || "—",
      remark: form.remark.trim() || "Active scholar in good standing",
      status: "Active",
      totalDisbursed: 0,
      imageUrl: form.imageUrl || "",
      createdDate: new Date().toISOString().split("T")[0],
      phone: form.phone || "—",
      email: form.email || "",
      avatarClass,
      initials
    }, nextIndex);

    const updated = [newRecord, ...scholarships];
    saveToStorage(updated);
    toast.success("Scholarship awarded and registered successfully!");

    if (window.bootstrap?.Modal && addModalRef.current) {
      window.bootstrap.Modal.getInstance(addModalRef.current)?.hide();
    }
  };

  // Open Edit Modal
  const handleOpenEditModal = (item, e) => {
    if (e) e.stopPropagation();
    setSelectedScholarship(item);

    const isKnownInst = INSTITUTIONS_LIST.includes(item.institution_name);
    const isKnownBank = POPULAR_BANKS_LIST.includes(item.bank_name);

    setForm({
      serial_number: item.serial_number || "",
      academic_session: item.academic_session || "2025/2026",
      institution_name: isKnownInst ? item.institution_name : "Other / External Institution",
      custom_institution_name: isKnownInst ? "" : item.institution_name || "",
      student_name: item.student_name || item.name || "",
      registration_number: item.registration_number || "",
      state_of_origin: item.state_of_origin || "Katsina",
      lga: item.lga || "Zango",
      course: item.course || "",
      level: String(item.level || "200").replace(/\D/g, "") || "200",
      scholarship_duration: item.scholarship_duration || "4 yrs",
      year_of_entry: item.year_of_entry || 2024,
      year_of_graduation: item.year_of_graduation || 2028,
      tuition_or_reg_fees: item.tuition_or_reg_fees ?? item.schoolFeesAmount ?? "",
      allowance: item.allowance ?? item.allowanceAmount ?? "",
      total_amount: item.total_amount ?? item.amount ?? "",
      bank_name: isKnownBank ? item.bank_name : "Other",
      custom_bank_name: isKnownBank ? "" : item.bank_name || "",
      account_number: item.account_number || item.bankAccount || "",
      remark: item.remark || "",
      imageUrl: item.imageUrl || "",
      imagePreview: item.imageUrl || null,
      phone: item.phone || "",
      email: item.email || ""
    });

    if (window.bootstrap?.Modal && editModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(editModalRef.current).show();
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!selectedScholarship) return;

    const finalInstitution = form.institution_name === "Other / External Institution"
      ? (form.custom_institution_name || "").trim()
      : form.institution_name;

    const finalBank = form.bank_name === "Other"
      ? (form.custom_bank_name || "").trim()
      : form.bank_name;

    const tuition = Number(form.tuition_or_reg_fees) || 0;
    const allowance = Number(form.allowance) || 0;
    const computedTotal = tuition + allowance;

    if (!form.student_name.trim()) {
      toast.error("Please enter student's full name.");
      return;
    }
    if (!finalInstitution) {
      toast.error("Please select or enter an institution.");
      return;
    }
    if (computedTotal <= 0) {
      toast.error("Please enter valid tuition or allowance amounts.");
      return;
    }

    const updated = scholarships.map((s) => {
      if (s.id === selectedScholarship.id) {
        return normalizeScholarship({
          ...s,
          serial_number: form.serial_number.trim() || s.serial_number,
          academic_session: form.academic_session || s.academic_session,
          institution_name: finalInstitution,
          student_name: form.student_name.trim(),
          registration_number: form.registration_number.trim().toUpperCase(),
          state_of_origin: form.state_of_origin,
          lga: form.lga,
          course: form.course.trim(),
          level: form.level,
          scholarship_duration: form.scholarship_duration,
          year_of_entry: Number(form.year_of_entry) || s.year_of_entry,
          year_of_graduation: Number(form.year_of_graduation) || s.year_of_graduation,
          tuition_or_reg_fees: tuition,
          allowance: allowance,
          total_amount: computedTotal,
          bank_name: finalBank,
          account_number: form.account_number.trim(),
          remark: form.remark.trim(),
          imageUrl: form.imageUrl || s.imageUrl || "",
          phone: form.phone || s.phone,
          email: form.email || s.email,
          initials: getInitials(form.student_name)
        });
      }
      return s;
    });

    saveToStorage(updated);
    toast.success("Scholarship profile updated successfully!");

    if (window.bootstrap?.Modal && editModalRef.current) {
      window.bootstrap.Modal.getInstance(editModalRef.current)?.hide();
    }
  };

  const hideModal = (ref) => {
    if (window.bootstrap?.Modal && ref.current) window.bootstrap.Modal.getInstance(ref.current)?.hide();
  };

  // Reusable 5-Section Scholarship Form
  const renderScholarshipForm = ({ formId, onSubmit, fileRef, isEdit }) => {
    const tuitionVal = Number(form.tuition_or_reg_fees) || 0;
    const allowanceVal = Number(form.allowance) || 0;
    const liveTotal = tuitionVal + allowanceVal;

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const setDigits = (k) => (e) => setForm({ ...form, [k]: e.target.value.replace(/\D/g, "") });

    const handleTuitionChange = (e) => {
      const val = e.target.value;
      const num = Number(val) || 0;
      setForm((prev) => ({
        ...prev,
        tuition_or_reg_fees: val,
        total_amount: num + (Number(prev.allowance) || 0)
      }));
    };

    const handleAllowanceChange = (e) => {
      const val = e.target.value;
      const num = Number(val) || 0;
      setForm((prev) => ({
        ...prev,
        allowance: val,
        total_amount: (Number(prev.tuition_or_reg_fees) || 0) + num
      }));
    };

    return (
      <form id={formId} onSubmit={onSubmit} className="d-flex flex-column gap-5">
        {/* Scholar Banner Preview */}
        <div className="d-flex align-items-center justify-content-between p-4 rounded-3 border bg-light">
          <div className="d-flex align-items-center gap-3 min-w-0">
            <button
              type="button"
              className="d6m-upload-preview flex-shrink-0"
              style={{ width: 56, height: 56, borderRadius: 999 }}
              onClick={() => fileRef.current?.click()}
              aria-label="Upload photo"
            >
              {form.imageUrl ? (
                <img src={form.imageUrl} alt="" style={{ borderRadius: 999, width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <i className="ki-outline ki-camera fs-3"></i>
              )}
            </button>
            <div className="min-w-0">
              <div className="fw-bold text-gray-900 fs-6 text-truncate">
                {form.student_name ? toTitleCase(form.student_name) : "New Beneficiary Name"}
              </div>
              <div className="text-gray-500 fs-8 text-truncate">
                {form.registration_number ? <span className="badge badge-light-primary me-2">{form.registration_number}</span> : null}
                <span>{form.institution_name === "Other / External Institution" ? (form.custom_institution_name || "Custom Institution") : form.institution_name}</span>
              </div>
            </div>
          </div>
          <div className="text-end flex-shrink-0 ps-3">
            <span className="d6m-k d-block mb-0">Total Grant</span>
            <span className="fs-5 fw-bolder text-success">{fmtCurrency(liveTotal)}</span>
          </div>
          <input type="file" ref={fileRef} className="d-none" accept="image/*" onChange={handleImageSelect} />
        </div>

        {/* General & Academic Information */}
        <div className="card border p-4 rounded-3 shadow-none">
          <div className="mb-3 pb-2 border-bottom">
            <h6 className="fw-bolder text-gray-900 m-0">General &amp; Academic Information</h6>
          </div>
          <div className="row g-3">
            <Field className="col-12 col-md-6" label="Academic Session" required>
              <select className="d6m-input" value={form.academic_session} onChange={setField("academic_session")}>
                {ACADEMIC_SESSIONS_LIST.map((sess) => (
                  <option key={sess} value={sess}>{sess} Academic Session</option>
                ))}
              </select>
            </Field>

            <Field className="col-12 col-md-6" label="Institution / School Name" required>
              <select className="d6m-input" value={form.institution_name} onChange={setField("institution_name")}>
                {INSTITUTIONS_LIST.map((inst) => (
                  <option key={inst} value={inst}>{inst}</option>
                ))}
              </select>
            </Field>

            {form.institution_name === "Other / External Institution" && (
              <Field className="col-12" label="Custom Institution Name" required>
                <input
                  type="text"
                  className="d6m-input"
                  placeholder="e.g. Al-Qalam University, Katsina"
                  required
                  value={form.custom_institution_name}
                  onChange={setField("custom_institution_name")}
                />
              </Field>
            )}

            <Field className="col-12 col-md-6" label="Course of Study / Programme" required>
              <input
                type="text"
                className="d6m-input"
                placeholder="e.g. B.Sc Biology Sci, Software Engr, BNSc Nursing"
                required
                value={form.course}
                onChange={setField("course")}
              />
            </Field>

            <Field className="col-12 col-md-6" label="Registration / Matric Number" required>
              <input
                type="text"
                className="d6m-input"
                placeholder="e.g. SAT/BIO/24/0022, AHS/NUS/24/1010"
                required
                value={form.registration_number}
                onChange={setField("registration_number")}
              />
            </Field>

            <Field className="col-6 col-md-3" label="Level / Current Year" required>
              <select className="d6m-input" value={form.level} onChange={setField("level")}>
                {LEVELS_LIST.map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl} Level</option>
                ))}
              </select>
            </Field>

            <Field className="col-6 col-md-3" label="Scholarship Duration" required>
              <select className="d6m-input" value={form.scholarship_duration} onChange={setField("scholarship_duration")}>
                {SCHOLARSHIP_DURATIONS_LIST.map((dur) => (
                  <option key={dur} value={dur}>{dur}</option>
                ))}
              </select>
            </Field>

            <Field className="col-6 col-md-3" label="Year of Entry" required>
              <input
                type="number"
                min="2018"
                max="2035"
                className="d6m-input"
                placeholder="e.g. 2025"
                required
                value={form.year_of_entry}
                onChange={setField("year_of_entry")}
              />
            </Field>

            <Field className="col-6 col-md-3" label="Year of Graduation" required>
              <input
                type="number"
                min="2020"
                max="2040"
                className="d6m-input"
                placeholder="e.g. 2028, 2029"
                required
                value={form.year_of_graduation}
                onChange={setField("year_of_graduation")}
              />
            </Field>
          </div>
        </div>

        {/* Student Bio-Data & Origin */}
        <div className="card border p-4 rounded-3 shadow-none">
          <div className="mb-3 pb-2 border-bottom">
            <h6 className="fw-bolder text-gray-900 m-0">Student Bio-Data &amp; Origin</h6>
          </div>
          <div className="row g-3">
            <Field className="col-12" label="Full Name" required>
              <input
                type="text"
                className="d6m-input"
                placeholder="e.g. Amina Bello, Ibrahim Musa"
                required
                value={form.student_name}
                onChange={setField("student_name")}
              />
            </Field>

            <Field className="col-12 col-md-6" label="State of Origin" required>
              <select className="d6m-input" value={form.state_of_origin} onChange={setField("state_of_origin")}>
                {STATES_LIST.map((st) => (
                  <option key={st} value={st}>{st} State</option>
                ))}
              </select>
            </Field>

            <Field className="col-12 col-md-6" label="LGA (Local Government Area)" required>
              {form.state_of_origin === "Katsina" ? (
                <select className="d6m-input" value={form.lga} onChange={setField("lga")}>
                  {KATSINA_LGAS.map((lga) => (
                    <option key={lga} value={lga}>{lga}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="d6m-input"
                  placeholder="e.g. Baure, Daura, Dutsi, Zango, Funtua"
                  required
                  value={form.lga}
                  onChange={setField("lga")}
                />
              )}
            </Field>

            <Field className="col-12 col-md-6" label="Phone Number (Optional)">
              <input
                type="text"
                className="d6m-input"
                placeholder="e.g. +234 802 345 6789"
                value={form.phone}
                onChange={setField("phone")}
              />
            </Field>

            <Field className="col-12 col-md-6" label="Email Address (Optional)">
              <input
                type="email"
                className="d6m-input"
                placeholder="e.g. scholar@futbabura.edu.ng"
                value={form.email}
                onChange={setField("email")}
              />
            </Field>
          </div>
        </div>

        {/* Financial & Fee Breakdown */}
        <div className="card border p-4 rounded-3 shadow-none">
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
            <h6 className="fw-bolder text-gray-900 m-0">Financial &amp; Fee Breakdown</h6>
            <span className="badge badge-light-success fs-8 fw-bold">Auto-Calculated Total</span>
          </div>
          <div className="row g-3">
            <Field className="col-12 col-md-6" label="Tuition Fees / Registration Fees (₦)" required hint="Semester or annual tuition / registration fees">
              <input
                type="number"
                min="0"
                className="d6m-input"
                placeholder="e.g. 110000"
                required
                value={form.tuition_or_reg_fees}
                onChange={handleTuitionChange}
              />
            </Field>

            <Field className="col-12 col-md-6" label="Allowance (₦)" required hint="Living / stipend / book allowance">
              <input
                type="number"
                min="0"
                className="d6m-input"
                placeholder="e.g. 70000"
                required
                value={form.allowance}
                onChange={handleAllowanceChange}
              />
            </Field>

            <div className="col-12">
              <div className="p-3 bg-light-success rounded-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="fw-bold text-gray-700 d-block fs-7">Total Scholarship Amount (Tuition + Allowance)</span>
                  <span className="fs-8 text-gray-500">Auto-calculated sum disbursed per funding cycle</span>
                </div>
                <div className="fs-3 fw-bolder text-success">
                  {fmtCurrency(liveTotal)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disbursement & Banking Details */}
        <div className="card border p-4 rounded-3 shadow-none">
          <div className="mb-3 pb-2 border-bottom">
            <h6 className="fw-bolder text-gray-900 m-0">Disbursement &amp; Banking Details</h6>
          </div>
          <div className="row g-3">
            <Field className="col-12 col-md-6" label="Bank Name" required>
              <select className="d6m-input" value={form.bank_name} onChange={setField("bank_name")}>
                {POPULAR_BANKS_LIST.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
                <option value="Other">Other Bank</option>
              </select>
            </Field>

            {form.bank_name === "Other" && (
              <Field className="col-12 col-md-6" label="Custom Bank Name" required>
                <input
                  type="text"
                  className="d6m-input"
                  placeholder="Enter Bank Name"
                  required
                  value={form.custom_bank_name}
                  onChange={setField("custom_bank_name")}
                />
              </Field>
            )}

            <Field className="col-12 col-md-6" label="Account Number (10 Digits)" required>
              <input
                type="text"
                maxLength={10}
                className="d6m-input"
                placeholder="e.g. 1012345678"
                required
                value={form.account_number}
                onChange={setDigits("account_number")}
              />
            </Field>
          </div>
        </div>

        {/* Administrative & Metadata */}
        <div className="card border p-4 rounded-3 shadow-none">
          <div className="mb-3 pb-2 border-bottom">
            <h6 className="fw-bolder text-gray-900 m-0">Administrative &amp; Metadata</h6>
          </div>
          <div className="row g-3">
            <Field className="col-12 col-md-4" label="Serial Number (S/N)" hint="Auto-generated unique ID">
              <input
                type="text"
                className="d6m-input"
                placeholder="e.g. SCH-2025-001"
                value={form.serial_number}
                onChange={setField("serial_number")}
              />
            </Field>

            <Field className="col-12 col-md-8" label="Remark" hint="Special approvals or observations">
              <input
                type="text"
                className="d6m-input"
                placeholder="e.g. Active scholar in good academic standing, recommended for grant"
                value={form.remark}
                onChange={setField("remark")}
              />
            </Field>
          </div>
        </div>
      </form>
    );
  };

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          {/* Header */}
          <div
            id="kt_app_header"
            className="app-header"
            data-kt-sticky="true"
            data-kt-sticky-activate="{default: false, lg: true}"
            data-kt-sticky-name="app-header-sticky"
            data-kt-sticky-offset="{default: false, lg: '300px'}"
          >
            <div
              className="app-container container-fluid d-flex flex-stack align-items-center my-6"
              id="kt_app_header_container"
            >
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div
                  className="btn btn-icon btn-active-color-primary w-35px h-35px me-2"
                  id="kt_app_sidebar_mobile_toggle"
                >
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>

              <PageHeader title="Scholarship Management" breadcrumb="Scholarships">
                <button
                  type="button"
                  className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                  onClick={handleOpenAddModal}
                >
                  <i className="ki-outline ki-plus fs-6 me-sm-2 text-white"></i>
                  <span className="d-none d-sm-inline">Award Scholarship</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <KpiStrip
                      loading={isLoading}
                      items={[
                        { icon: "ki-people", label: "Total Beneficiaries", value: stats.total, sub: "Registered scholars" },
                        { icon: "ki-check-circle", label: "Active Awards", value: stats.active, sub: "Currently funded" },
                        { icon: "ki-wallet", label: "Total Disbursed", value: fmtCurrency(stats.disbursed), sub: "Paid to scholars" },
                        { icon: "ki-bank", label: "Committed Budget", value: fmtCurrency(stats.totalBudget), sub: "Tuition & allowance total" },
                      ]}
                    />

                    <div className="d6-card p-6 mb-5">
                      <CardHead title="Scholarship Beneficiaries" subtitle="Search and manage academic awards, origin, and fee breakdown" className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
                          <input
                            className="d6m-input ps-10"
                            style={{ width: 280, minHeight: 40, height: 40, borderRadius: 999 }}
                            placeholder="Name, matric, school, LGA…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </div>
                        <select
                          className="d6m-input"
                          style={{ width: 180, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={sessionFilter}
                          onChange={(e) => setSessionFilter(e.target.value)}
                        >
                          <option value="all">All Sessions</option>
                          {ACADEMIC_SESSIONS_LIST.map((sess) => (
                            <option key={sess} value={sess}>{sess} Session</option>
                          ))}
                        </select>
                        <select
                          className="d6m-input"
                          style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="Active">Active</option>
                          <option value="Paused">Paused</option>
                          <option value="Completed">Completed</option>
                        </select>
                        <select
                          className="d6m-input"
                          style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                          value={stateFilter}
                          onChange={(e) => setStateFilter(e.target.value)}
                        >
                          <option value="all">All States</option>
                          <option value="Katsina">Katsina</option>
                          <option value="Kaduna">Kaduna</option>
                          <option value="Kano">Kano</option>
                          <option value="Kebbi">Kebbi</option>
                          <option value="Jigawa">Jigawa</option>
                          <option value="Sokoto">Sokoto</option>
                          <option value="Zamfara">Zamfara</option>
                        </select>
                        <div className="d6-seg">
                          <button
                            type="button"
                            className={viewMode === "list" ? "active" : ""}
                            onClick={() => setViewMode("list")}
                            aria-label="List view"
                          >
                            <i className="ki-outline ki-row-horizontal fs-5"></i>
                          </button>
                          <button
                            type="button"
                            className={viewMode === "card" ? "active" : ""}
                            onClick={() => setViewMode("card")}
                            aria-label="Card view"
                          >
                            <i className="ki-outline ki-element-11 fs-5"></i>
                          </button>
                        </div>
                      </CardHead>

                      {isLoading ? (
                        <div className="d6m-empty py-10">
                          <span className="spinner-border spinner-border-sm me-2"></span>Loading records...
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="d6m-empty py-12">
                          <i className="ki-outline ki-teacher fs-3x text-gray-300 d-block mb-3"></i>
                          <div className="d6-title text-gray-900 mb-1">No scholarship records found.</div>
                          <div className="fs-8 text-gray-500">Click &ldquo;Award Scholarship&rdquo; to register a new beneficiary.</div>
                        </div>
                      ) : viewMode === "list" ? (
                        <div className="d-flex flex-column gap-2">
                          {filtered.map((s) => (
                            <div
                              key={s.id}
                              className="d6-card d6-list-card d-flex align-items-center py-4 px-5 cursor-pointer transition-all border-hover-success position-relative overflow-hidden"
                              style={{ minHeight: 76 }}
                              onClick={() => navigate(`/scholarship/${s.id}`)}
                            >
                              {/* Beneficiary & Institution */}
                              <div className="d-flex align-items-center min-w-0" style={{ width: 340, flexShrink: 0 }}>
                                <Avatar src={s.imageUrl} name={s.student_name || s.name} size={44} />
                                <div className="d-flex flex-column overflow-hidden min-w-0 ms-3">
                                  <span className="fs-6 fw-bold text-gray-900 text-truncate">
                                    {toTitleCase(s.student_name || s.name)}
                                  </span>
                                  <span className="text-gray-500 fs-8 text-truncate">
                                    <span className="fw-semibold text-primary me-2">{s.registration_number}</span>
                                    <span>{s.institution_name}</span>
                                  </span>
                                </div>
                              </div>

                              {/* Course & Level */}
                              <div className="d-none d-lg-flex flex-column min-w-0 ms-4 overflow-hidden" style={{ width: 220, flexShrink: 0 }}>
                                <span className="fw-semibold text-gray-800 fs-7 text-truncate">
                                  {s.course}
                                </span>
                                <span className="text-gray-500 fs-8">
                                  {s.level} Level · {s.academic_session}
                                </span>
                              </div>

                              {/* Total Grant & Status */}
                              <div className="d-flex align-items-center gap-4 text-end ms-auto flex-shrink-0">
                                <div className="text-end">
                                  <div className="d6m-k mb-0">Total Grant</div>
                                  <div className="fw-bold text-success fs-6">{fmtCurrency(s.total_amount)}</div>
                                </div>
                                <StatusChip color={s.status === "Active" ? "#16A34A" : s.status === "Paused" ? "#D97706" : "#2563EB"}>
                                  {s.status}
                                </StatusChip>
                                <button
                                  type="button"
                                  className="btn btn-icon btn-sm btn-light-primary rounded-circle"
                                  title="Edit scholar"
                                  onClick={(e) => handleOpenEditModal(s, e)}
                                >
                                  <i className="ki-outline ki-pencil fs-6"></i>
                                </button>
                                <i className="ki-outline ki-right fs-5 text-gray-400 d-none d-sm-inline"></i>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="row g-5">
                          {filtered.map((s) => (
                            <div className="col-12 col-md-6 col-xl-4" key={s.id}>
                              <div
                                className="d6-card border-hover-success h-100 d-flex flex-column cursor-pointer transition-all p-5"
                                onClick={() => navigate(`/scholarship/${s.id}`)}
                              >
                                <div className="d-flex align-items-start justify-content-between mb-3">
                                  <div className="d-flex align-items-center gap-3 min-w-0">
                                    <Avatar src={s.imageUrl} name={s.student_name || s.name} size={48} />
                                    <div className="min-w-0">
                                      <h5 className="d6-title text-gray-900 mb-0 text-truncate">
                                        {toTitleCase(s.student_name || s.name)}
                                      </h5>
                                      <span className="text-primary fw-semibold fs-8">{s.registration_number}</span>
                                    </div>
                                  </div>
                                  <StatusChip color={s.status === "Active" ? "#16A34A" : s.status === "Paused" ? "#D97706" : "#2563EB"}>
                                    {s.status}
                                  </StatusChip>
                                </div>

                                <div className="d-flex flex-column gap-1 mb-4">
                                  <span className="fw-semibold text-gray-900 fs-7 text-truncate">{s.institution_name}</span>
                                  <span className="text-gray-500 fs-8 text-truncate">{s.course} · {s.level} Level</span>
                                </div>

                                <div className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top">
                                  <span className="text-gray-500 fs-8">{s.state_of_origin} ({s.lga})</span>
                                  <span className="fs-6 fw-bold text-success">{fmtCurrency(s.total_amount)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="my-10"></div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                  <div className="text-gray-900 text-center">
                    <span className="text-muted fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL: Award/Add Scholarship */}
      <ModalShell
        modalRef={addModalRef}
        icon="ki-award"
        title="Award New Scholarship"
        onClose={() => hideModal(addModalRef)}
        width={780}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(addModalRef)}>Cancel</button>
            <button type="submit" form="award-scholarship-form" className="btn btn-success d6-mbtn">
              <i className="ki-outline ki-check-circle fs-5 me-2 text-white"></i>Award Scholarship
            </button>
          </>
        }
      >
        {renderScholarshipForm({ formId: "award-scholarship-form", onSubmit: handleAddSubmit, fileRef: addFileInputRef, isEdit: false })}
      </ModalShell>

      {/* MODAL: Edit Scholarship */}
      <ModalShell
        modalRef={editModalRef}
        icon="ki-pencil"
        title="Edit Scholarship Details"
        onClose={() => hideModal(editModalRef)}
        width={780}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(editModalRef)}>Cancel</button>
            <button type="submit" form="edit-scholarship-form" className="btn btn-success d6-mbtn">
              <i className="ki-outline ki-check fs-5 me-2 text-white"></i>Save Changes
            </button>
          </>
        }
      >
        {renderScholarshipForm({ formId: "edit-scholarship-form", onSubmit: handleEditSubmit, fileRef: editFileInputRef, isEdit: true })}
      </ModalShell>
    </>
  );
}
